import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Flex, Group, Paper, Stack, Text } from '@mantine/core'
import { faMinus, faPlus } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { faFloppyDisk } from '@fortawesome/sharp-duotone-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionIcon } from '../Common/ActionIcon'
import { Button } from '../Common/Button'
import { getTaskUrgency, type TaskUrgency } from '../../utils/taskUrgency'
import { isTaskOpen, toDateInputValue, todayDateInputValue } from './TaskCardHelpers'
import { TaskCardHeader } from './TaskCardHeader'
import { TaskEventReminder } from './TaskEventReminder'
import { TaskHoldDateRow } from './TaskHoldDateRow'
import { DESCRIPTION_MAX_LENGTH, TaskHoldDescription } from './TaskHoldDescription'
import { TaskStatusRow } from './TaskStatusRow'
import type { ClaimTask, TaskHoldValues } from '../../types/task'

export { DESCRIPTION_MAX_LENGTH }

export interface TaskCardProps {
  task: ClaimTask
  // Puts the task on hold. The On Hold Until date and the description travel in a
  // single PATCH, so Save is the one submit point — the status and date edits before
  // it are local until then. Resolves true only when the save succeeded; the card
  // clears and collapses on nothing less.
  onHold?: (taskId: string, values: TaskHoldValues) => Promise<boolean>
  // True while this task's hold is in flight.
  saving?: boolean
  // Message from a failed hold, shown under the description.
  error?: string | null
  // Opens Outlook's New Event form pre-filled from this task (MSD-684).
  onAddEventReminder?: (task: ClaimTask) => void
  // Why the reminder button is disabled (compose mode, mobile, unsupported host).
  // Null/undefined leaves it enabled.
  reminderDisabledReason?: string | null
  // True while this task's event form is being opened.
  addingReminder?: boolean
  // Outcome of the last reminder attempt on this card.
  reminderFeedback?: { status: 'success' | 'error'; message: string } | null
}

// Four-digit years only: below this a year is still being typed, above it the native
// input has accepted more digits than a year has (it allows up to 275760).
const MIN_DATE_YEAR = 1000
const MAX_DATE_YEAR = 9999

function getUrgencyBorder(urgency: TaskUrgency): CSSProperties | undefined {
  if (urgency === 'overdue') return { borderColor: 'var(--mantine-color-danger-6)', borderWidth: 2 }
  if (urgency === 'nearlyDue') return { borderColor: 'var(--mantine-color-warning-6)', borderWidth: 2 }
  return undefined
}

// MC1's task description, shown read-only on the expanded card. Empty values follow the
// app's convention: null reads "No Record", an empty string "N/A".
function getReadOnlyDescription(value: string | null | undefined): ReactNode {
  if (value == null) {
    return (
      <Text span inherit c="var(--link-color)" fs="italic">
        No Record
      </Text>
    )
  }
  if (value === '') {
    return (
      <Text span inherit c="var(--link-color)">
        N/A
      </Text>
    )
  }
  return value
}

export function TaskCard({
  task,
  onHold,
  saving = false,
  error = null,
  onAddEventReminder,
  reminderDisabledReason = null,
  addingReminder = false,
  reminderFeedback = null,
}: Readonly<TaskCardProps>) {
  const [expanded, setExpanded] = useState(false)
  const [editingHoldDate, setEditingHoldDate] = useState(false)
  // The On hold Description starts empty on every hold: MC1 writes it to the task's
  // on-hold history, which no proc reads back, so there is no saved value to seed it.
  const [description, setDescription] = useState('')
  // A hold opened on the card but not yet saved. It drives the hold date and description
  // editors only — the status label keeps showing MC1's value until the save lands.
  const [heldLocally, setHeldLocally] = useState(false)
  // Likewise the rescheduled hold date, so the label reflects the pick immediately.
  const [holdDateOverride, setHoldDateOverride] = useState<string | null>(null)
  // Why the open date field holds a date the card declined (an unfinished year, or a day
  // already gone), so it no longer matches the label or what Save would send.
  const [holdDateError, setHoldDateError] = useState<string | null>(null)
  const holdDateInputRef = useRef<HTMLInputElement>(null)

  const isOnHold = task.isOnHold || heldLocally
  // The on-hold proc refuses any update to a task whose On Hold Until is still ahead
  // (Msg 108), whatever is being changed, so a hold in force is read-only here. The date
  // goes to the proc as midnight, so "still ahead" is a hold date later than today. Once
  // that date passes the proc accepts a new hold, and so does the card.
  const holdInForce = toDateInputValue(task.holdDate) > todayDateInputValue()
  // Only an Open task may be placed on hold, so MC1's terminal statuses (Cancelled,
  // Closed) get no status pencil — there is nothing the card can set on them.
  const canPutOnHold = isTaskOpen(task) && !holdInForce

  // The On Hold Until date is the only date the update endpoint can set, so it is the
  // only one the card lets the user change — the target date is MC1's and read-only
  // here. A date the user picked wins, otherwise the saved hold date stands. There is
  // deliberately no target-date fallback: a task can be held with no On Hold Until
  // date at all, and showing its target date there would read as a hold date MC1
  // never set.
  const effectiveHoldDate = holdDateOverride ?? task.holdDate ?? null
  // Once the task prop comes back carrying the date we picked, the change has
  // landed — stop flagging it as pending so the highlight doesn't stick forever.
  const holdDateChanged = holdDateOverride !== null && toDateInputValue(task.holdDate) !== holdDateOverride
  // The border follows the target date (MSD-683) — rescheduling a hold here never moves
  // it. Closed and Cancelled tasks get none.
  const urgency = getTaskUrgency(task)

  // The hold date sits on its own row, hidden for a task that is neither held nor
  // being held, and hidden when there is no date to show. Expanding reveals it even
  // when empty, because that is the only anchor for the pencil that sets one.
  const showHoldDate = isOnHold && (expanded || effectiveHoldDate != null)
  // The On hold Description field and the Save/Cancel pair that submits it appear
  // together. A hold in force is read-only, so it gets neither.
  const holdEditorOpen = expanded && isOnHold && !holdInForce

  // Opening the native picker is imperative, so it runs once the input is mounted.
  // showPicker throws if the browser doesn't consider this user-initiated.
  useEffect(() => {
    if (!editingHoldDate) return
    try {
      holdDateInputRef.current?.showPicker?.()
    } catch {
      // Falls back to the visible date field, which has its own calendar button.
    }
  }, [editingHoldDate])

  // Hold is the only status the card can set, so the pencil goes straight to the hold
  // date and description editors — a confirm step in between chose nothing. Nothing is
  // sent until Save, because the API takes the status, date and description at once.
  // The pencil shows on a collapsed card too, so it expands the card as well: everything
  // the hold reveals lives in the expanded body.
  //
  // On a task MC1 already holds (the pencil only shows there once the hold date has
  // passed) it only opens those editors: setting heldLocally there
  // would count as a pending edit, flagging "Unsaved" and arming Cancel before the user
  // has changed anything.
  const openStatusEdit = () => {
    setExpanded(true)
    if (!task.isOnHold) setHeldLocally(true)
  }

  // Picking a date updates the label straight away, but deliberately leaves the field
  // open — the pencil closes it. A native date input fires a change on every segment
  // that completes, which is not the same as the user being done: on a field already
  // holding a date, editing one segment forms a valid date on the very first
  // keystroke, so closing here used to snatch the field away mid-edit and commit a
  // date that was never picked.
  //
  // The year still needs guarding on its own, at both ends. Typed digit by digit it
  // arrives zero-padded — 0002-09-15 / 0202-09-15 before 2026-09-15 — and committing
  // the first would persist year 2 and blank the label. A date input also accepts
  // more than four digits (202568-12-04), so the year is read up to the separator
  // rather than sliced at four characters, which took that for 2025 and committed it.
  //
  // A declined year stays on screen, because the field is uncontrolled — so it is flagged,
  // and Save is held back until the field agrees with the label again. An empty value is
  // deliberately not flagged: the host reports every incomplete date that way, so a field
  // being typed into from blank would show the error before the user had finished.
  //
  // The on-hold proc only accepts a hold date later than today, so today and any past
  // day are declined the same way rather than saved and refused. The field's min greys
  // those out in the calendar, but typing one in is still possible.
  // Once the year is known to be four digits, yyyy-mm-dd strings compare as dates.
  const handleHoldDateSelect = (value: string) => {
    if (!value) return
    const year = Number(value.split('-')[0])
    if (year < MIN_DATE_YEAR || year > MAX_DATE_YEAR) {
      setHoldDateError('Enter a valid date')
      return
    }
    if (value <= todayDateInputValue()) {
      setHoldDateError('Hold date must be after today')
      return
    }
    setHoldDateError(null)
    setHoldDateOverride(value)
  }

  // Each open remounts the field on the committed date, and a closed field shows nothing,
  // so the flag only counts while it is open and is dropped whenever the pencil toggles it.
  const toggleHoldDateEdit = () => {
    setEditingHoldDate((open) => !open)
    setHoldDateError(null)
  }
  const holdDateFieldError = editingHoldDate ? holdDateError : null
  const holdDateInvalid = holdDateFieldError !== null

  const isDescriptionTooLong = description.length > DESCRIPTION_MAX_LENGTH
  // The PATCH always carries an On Hold Until date, so a task with none saved needs one
  // picked before it can go. Now that the target date is no longer used as a fallback,
  // there is nothing to send until then — and sending it silently would write a date
  // the card never showed.
  const hasHoldDate = effectiveHoldDate != null

  // Anything the user has changed but not saved: a hold opened on the card, a date moved
  // off the saved one, or a description typed. Both buttons are dead until then —
  // Cancel has nothing to drop, and Save would PATCH the values MC1 already holds.
  const hasHoldEdits = heldLocally || holdDateChanged || description !== ''
  const canSubmitHold =
    hasHoldEdits && description.trim().length > 0 && !isDescriptionTooLong && hasHoldDate && !holdDateInvalid && !saving
  // Only worth flagging once the card is closed on it — while the editor is open, Save
  // and Cancel sit right there saying the same thing.
  const showUnsaved = !expanded && hasHoldEdits
  // A declined date never reaches the label, so it isn't a hold edit — but the field and
  // its error are still on screen, and Cancel is the obvious way to back out of them.
  const canCancelHold = (hasHoldEdits || holdDateInvalid) && !saving

  // Cancel discards the whole pending edit — the typed description, the picked date and a
  // hold set from the pencil — exactly as Present Position's Cancel drops its draft. A task
  // MC1 already holds is left open on its saved values. An Open task held from the pencil
  // collapses too: dropping the hold returns it to its plain state, which has no editors
  // and no −/+ to close them with. The −/+ toggle is the way to close on a draft and keep
  // it. Without this an accidental pencil click could never be taken back: nothing else
  // clears heldLocally short of a successful save.
  const cancelHoldEdit = () => {
    setDescription('')
    setHoldDateOverride(null)
    setHeldLocally(false)
    setEditingHoldDate(false)
    setHoldDateError(null)
    if (!task.isOnHold) setExpanded(false)
  }

  // The single submit: the On Hold Until date and the description in one PATCH. The
  // card only clears and collapses once the save is confirmed, so a failure leaves
  // the typed description in place to retry.
  const submitHold = async () => {
    if (!canSubmitHold || effectiveHoldDate == null) return

    const saved = await onHold?.(task.id, {
      onHoldUntilDate: toDateInputValue(effectiveHoldDate),
      holdDescription: description.trim(),
    })
    if (saved !== true) return

    setDescription('')
    setHoldDateOverride(null)
    setHeldLocally(false)
    setExpanded(false)
    setEditingHoldDate(false)
  }

  // Collapsing closes the hold-date editor. A typed description is
  // deliberately kept, so an accidental collapse doesn't throw away the draft.
  const toggleExpanded = () => {
    const next = !expanded
    setExpanded(next)
    if (!next) setEditingHoldDate(false)
  }

  return (
    <Paper
      withBorder
      p="xs"
      shadow="xs"
      style={{
        ...getUrgencyBorder(urgency),
        ...(showUnsaved ? { backgroundColor: 'var(--unsaved-bg)' } : {}),
      }}
    >
      {/* With the hold editor open, Save/Cancel take the full top row and the task details
          drop beneath them — beside the buttons the title and assignee truncate to a few
          characters. column-reverse rather than reordering the children keeps the tree
          stable, so the −/+ button isn't remounted (and doesn't lose focus) on toggle. */}
      <Flex
        direction={holdEditorOpen ? 'column-reverse' : 'row'}
        justify="space-between"
        align={holdEditorOpen ? 'stretch' : 'flex-start'}
        gap="xs"
      >
        <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
          <TaskCardHeader type={task.type} assignedTo={task.assignedTo} urgency={urgency} unsaved={showUnsaved} />
          <TaskStatusRow
            taskType={task.type}
            status={task.status}
            targetDate={task.targetDate}
            canEdit={canPutOnHold}
            onEdit={openStatusEdit}
          />
          {showHoldDate && (
            <TaskHoldDateRow
              taskType={task.type}
              holdDate={effectiveHoldDate}
              changed={holdDateChanged}
              expanded={expanded}
              canEdit={!holdInForce}
              editing={editingHoldDate}
              error={holdDateFieldError}
              inputRef={holdDateInputRef}
              onToggleEdit={toggleHoldDateEdit}
              onSelect={handleHoldDateSelect}
            />
          )}
          {/* What expanding is for on every card, held or not. */}
          {expanded && (
            <Stack gap={0}>
              <Text fz="xs" c="dimmed">
                Task Description
              </Text>
              <Text fz="xs" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {getReadOnlyDescription(task.taskDescription)}
              </Text>
            </Stack>
          )}
        </Stack>
        {/* Save/Cancel for the hold editor sit up here beside the collapse icon, so they
            stay in view however long the description grows. */}
        <Group gap={4} wrap="nowrap" align="center" justify="flex-end">
          {holdEditorOpen && (
            <>
              <Button
                label="Cancel"
                size="xs"
                variant="outline"
                aria-label={`Cancel hold edit for task ${task.type}`}
                disabled={!canCancelHold}
                onClick={cancelHoldEdit}
              />
              <Button
                label="Save"
                size="xs"
                variant="filled"
                aria-label={`Save description for task ${task.type}`}
                loading={saving}
                disabled={!canSubmitHold}
                onClick={submitHold}
                rightSection={<FontAwesomeIcon icon={faFloppyDisk} />}
              />
            </>
          )}
          <ActionIcon
            variant="subtle"
            size="lg"
            icon={expanded ? faMinus : faPlus}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} task ${task.type}`}
            onClick={toggleExpanded}
          />
        </Group>
      </Flex>
      {/* Outside the row above so the field spans the whole card rather than stopping at
          the Save/Cancel column. */}
      {holdEditorOpen && (
        <TaskHoldDescription
          taskType={task.type}
          value={description}
          onChange={setDescription}
          tooLong={isDescriptionTooLong}
          needsHoldDate={!hasHoldDate && description.trim().length > 0}
          error={error}
        />
      )}
      {/* Always on the card, collapsed or not — a reminder needs nothing the expanded
          view adds. */}
      <TaskEventReminder
        onClick={() => onAddEventReminder?.(task)}
        disabledReason={reminderDisabledReason}
        loading={addingReminder}
        feedback={reminderFeedback}
      />
    </Paper>
  )
}
